import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const INTERIOR_IMG = "/assets/kababish-storefront.webp";

const chapters = [
  {
    num: "01",
    title: "Fire & Tradition",
    copy: "Our tandoor burns from morning to close. Kebabs are marinated overnight, skewered by hand and finished over real charcoal — the way it has been done for centuries.",
  },
  {
    num: "02",
    title: "Two Cuisines, One Table",
    copy: "From Lahori karahi and seekh kebabs to butter chicken and Hyderabadi biryani — we cook both sides of the border with equal devotion, and no shortcuts.",
  },
  {
    num: "03",
    title: "Calamvale's Own",
    copy: "A family restaurant on Beaudesert Road where every guest is fed like family. Dine in, take away, or let us bring the feast to your door.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.12, duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  }),
};

const About = () => {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: imgRef,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section id="about" data-testid="about-section" className="bg-cream py-24 sm:py-36">
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <motion.p
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          custom={0}
          className="font-script text-spice text-2xl sm:text-3xl mb-3"
        >
          our story
        </motion.p>
        <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          custom={1}
          className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-navy max-w-3xl leading-[1.05]"
        >
          Recipes carried across generations.
        </motion.h2>

        <div className="mt-16 sm:mt-20 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <motion.div
            ref={imgRef}
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 relative"
          >
            <div className="rounded-2xl overflow-hidden aspect-[4/3] shadow-2xl">
              <motion.img
                src={INTERIOR_IMG}
                alt="Kababish Calamvale restaurant storefront on Beaudesert Road"
                className="h-[115%] w-full object-cover"
                style={{ y: imgY }}
              />
            </div>
            <div className="absolute -bottom-6 -right-4 sm:-right-6 bg-navy text-cream rounded-2xl px-7 py-6 shadow-xl">
              <p className="font-heading text-4xl font-semibold text-gold">100%</p>
              <p className="text-xs tracking-[0.2em] uppercase text-cream/70 mt-1">Halal &amp; Authentic</p>
            </div>
          </motion.div>

          <div className="lg:col-span-7 flex flex-col gap-8 lg:pl-6">
            {chapters.map((c, i) => (
              <motion.article
                key={c.num}
                data-testid={`about-chapter-${c.num}`}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                custom={i}
                className={`group border border-navy/10 rounded-2xl p-8 sm:p-10 bg-beige hover:bg-navy transition-colors duration-500 ${
                  i % 2 === 1 ? "lg:ml-16" : ""
                }`}
              >
                <div className="flex items-start gap-6">
                  <span className="font-heading text-5xl sm:text-6xl font-semibold text-outline-navy group-hover:text-gold group-hover:[-webkit-text-stroke:0px] transition-all duration-500">
                    {c.num}
                  </span>
                  <div>
                    <h3 className="font-heading text-2xl sm:text-3xl font-semibold text-navy group-hover:text-gold transition-colors duration-500 tracking-tight">
                      {c.title}
                    </h3>
                    <p className="mt-3 text-ink/70 group-hover:text-cream/75 transition-colors duration-500 leading-relaxed font-light">
                      {c.copy}
                    </p>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
